(() => {
	const isDesktop = () => window.innerWidth >= 992;
	let layoutObserver = null;
	let openTimer = null;
	let closeTimer = null;
	let openItem = null;
	let featuredTimer = null;
	let featuredItem = null;

	const placeCategoryBar = () => {
		const bar = document.querySelector("[data-imek-category-bar]");
		const navbar = document.querySelector("header .navbar, .navbar");
		if (!bar || !navbar || bar.dataset.categoryBarPlaced) {
			return;
		}

		navbar.insertAdjacentElement("afterend", bar);
		bar.dataset.categoryBarPlaced = "1";
	};

	// Desktop header dimensions are fixed in CSS to avoid a self-referential
	// ResizeObserver loop. Only the separate mobile search row is measured.
	const syncNavbarHeight = () => {
		const search = document.querySelector(".imek-navbar-search.imek-sticky-search");
		if (!isDesktop() && search && search.offsetHeight) {
			[document.documentElement, document.body].forEach((element) => {
				element?.style.setProperty(
					"--imek-mobile-search-height",
					`${search.offsetHeight}px`
				);
			});
		}
	};

	// The mobile search row can change after a late-loading font. Observe that
	// row only; observing the fixed desktop header caused the original growth.
	const observeLayout = () => {
		if (!window.ResizeObserver || layoutObserver) return;
		layoutObserver = new ResizeObserver(() => syncNavbarHeight());
		const search = document.querySelector(".imek-navbar-search.imek-sticky-search");
		if (search) {
			layoutObserver.observe(search);
		}
	};

	const markActiveCategory = () => {
		const path = (window.location.pathname || "").replace(/\/+$/, "");
		const currentUrl = new URL(window.location.href);
		const categoryParam = (url) => (
			url.searchParams.get("webshop_category")
			|| url.searchParams.get("item_group")
			|| ""
		);
		document.querySelectorAll(".imek-category-item").forEach((item) => {
			const trigger = item.querySelector(".imek-category-trigger");
			if (!trigger) {
				return;
			}
			let href = trigger.getAttribute("href") || "";
			item.classList.remove("is-active");

			let triggerUrl = null;
			try {
				triggerUrl = new URL(href, window.location.origin);
			} catch (e) {
				triggerUrl = null;
			}

			if (!triggerUrl) {
				return;
			}

			const triggerPath = triggerUrl.pathname.replace(/\/+$/, "") || "/";
			const isRouteMatch = !triggerUrl.search
				&& triggerPath !== "/"
				&& (path === triggerPath || path.startsWith(triggerPath + "/"));
			const isQueryMatch = triggerUrl.search
				&& currentUrl.pathname === triggerUrl.pathname
				&& categoryParam(currentUrl)
				&& categoryParam(currentUrl) === categoryParam(triggerUrl);

			if (isRouteMatch || isQueryMatch) {
				item.classList.add("is-active");
			}
		});
	};

	const cancelOpenTimer = () => {
		if (openTimer) {
			window.clearTimeout(openTimer);
			openTimer = null;
		}
	};

	const cancelCloseTimer = () => {
		if (closeTimer) {
			window.clearTimeout(closeTimer);
			closeTimer = null;
		}
	};

	const setItemOpen = (item, shouldOpen) => {
		if (!item) return;
		item.classList.toggle("is-open", shouldOpen);
		item
			.querySelector(".imek-category-trigger")
			?.setAttribute("aria-expanded", shouldOpen ? "true" : "false");
	};

	const focusFirstMenuLink = (item) => {
		const link = (
			item?.querySelector(".imek-mega-slide.is-active:not([hidden])")
			|| item?.querySelector(".imek-mega-menu__content a")
		);
		link?.focus({ preventScroll: true });
	};

	const positionMenu = (item) => {
		const menu = item?.querySelector(".imek-mega-menu");
		const trigger = item?.querySelector(".imek-category-trigger");
		const inner = item?.closest(".imek-category-bar-inner");
		if (!menu || !trigger || !inner || !isDesktop()) return;

		const innerRect = inner.getBoundingClientRect();
		const triggerRect = trigger.getBoundingClientRect();
		const panelWidth = menu.getBoundingClientRect().width;
		const edgeGap = 24;
		const preferredLeft = (
			triggerRect.left
			+ triggerRect.width / 2
			- innerRect.left
			- panelWidth / 2
		);
		const maxLeft = Math.max(edgeGap, innerRect.width - panelWidth - edgeGap);
		const left = Math.min(Math.max(preferredLeft, edgeGap), maxLeft);
		menu.style.setProperty("--imek-mega-left", `${Math.round(left)}px`);
	};

	const featuredSlides = (item) => (
		[...(item?.querySelectorAll("[data-imek-mega-slide]") || [])]
			.filter((slide) => slide.dataset.invalidImage !== "1")
	);

	const pauseFeaturedRotation = () => {
		if (featuredTimer) {
			window.clearInterval(featuredTimer);
			featuredTimer = null;
		}
		featuredItem
			?.querySelector("[data-imek-mega-carousel]")
			?.classList.add("is-paused");
	};

	const stopFeaturedRotation = () => {
		pauseFeaturedRotation();
		featuredItem = null;
	};

	const setFeaturedSlide = (item, nextIndex) => {
		const slides = featuredSlides(item);
		if (!slides.length) return;
		const index = ((nextIndex % slides.length) + slides.length) % slides.length;

		item.querySelectorAll("[data-imek-mega-slide]").forEach((slide) => {
			const isActive = slide === slides[index];
			slide.classList.toggle("is-active", isActive);
			slide.setAttribute("aria-hidden", isActive ? "false" : "true");
			if (isActive) {
				slide.removeAttribute("tabindex");
			} else {
				slide.setAttribute("tabindex", "-1");
			}
		});
	};

	const collapseFeaturedMedia = (item) => {
		const menu = item?.querySelector(".imek-mega-menu");
		if (!menu) return;
		if (!menu.querySelector(".imek-mega-menu__content")) {
			stopFeaturedRotation();
			setItemOpen(item, false);
			if (openItem === item) openItem = null;
			item.dataset.megaMenuDisabled = "1";
			const trigger = item.querySelector(".imek-category-trigger");
			trigger?.removeAttribute("aria-haspopup");
			trigger?.removeAttribute("aria-controls");
			trigger?.removeAttribute("aria-expanded");
			menu.remove();
			return;
		}
		menu?.querySelector("[data-imek-mega-media]")?.remove();
		menu?.classList.remove("imek-mega-menu--has-featured");
		menu?.classList.add("imek-mega-menu--no-featured");
		stopFeaturedRotation();
		positionMenu(item);
	};

	const startFeaturedRotation = (item, reset = false) => {
		stopFeaturedRotation();
		const slides = featuredSlides(item);
		if (!slides.length) {
			if (item?.querySelector("[data-imek-mega-media]")) {
				collapseFeaturedMedia(item);
			}
			return;
		}

		featuredItem = item;
		const currentIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains("is-active")));
		setFeaturedSlide(item, reset ? 0 : currentIndex);
		item
			.querySelector("[data-imek-mega-carousel]")
			?.classList.remove("is-paused");

		const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		if (slides.length < 2 || reducedMotion || document.hidden) return;

		// Kept in step with the image zoom in _mega_nav.scss
		// (.imek-mega-slide img, imek-mega-product-motion). The flyout only
		// lives as long as the pointer rests on it, so a slow rotation meant
		// most visitors saw a single product and never learned the panel
		// cycles at all.
		featuredTimer = window.setInterval(() => {
			const currentSlides = featuredSlides(item);
			const activeIndex = currentSlides.findIndex((slide) => slide.classList.contains("is-active"));
			setFeaturedSlide(item, activeIndex + 1);
		}, 2000);
	};

	const closeMenus = (except = null) => {
		cancelOpenTimer();
		if (!except) cancelCloseTimer();
		if (!except || featuredItem !== except) stopFeaturedRotation();
		document.querySelectorAll(".imek-category-item.is-open").forEach((item) => {
			if (item !== except) {
				setItemOpen(item, false);
			}
		});
		openItem = except?.classList.contains("is-open") ? except : null;
	};

	const openMenu = (item, focusFirstLink = false) => {
		if (!isDesktop() || !item || item.dataset.megaMenuDisabled === "1") return;
		cancelOpenTimer();
		cancelCloseTimer();
		if (item === openItem && item.classList.contains("is-open")) {
			positionMenu(item);
			if (focusFirstLink) {
				focusFirstMenuLink(item);
				pauseFeaturedRotation();
			}
			return;
		}
		closeMenus(item);
		setItemOpen(item, true);
		positionMenu(item);
		openItem = item;
		startFeaturedRotation(item, true);

		if (focusFirstLink) {
			window.requestAnimationFrame(() => {
				focusFirstMenuLink(item);
			});
		}
	};

	const queueOpen = (item) => {
		cancelOpenTimer();
		cancelCloseTimer();
		if (item === openItem && item.classList.contains("is-open")) return;
		const delay = openItem && openItem !== item ? 60 : 120;
		openTimer = window.setTimeout(() => {
			openTimer = null;
			openMenu(item);
		}, delay);
	};

	const queueClose = (item) => {
		cancelOpenTimer();
		cancelCloseTimer();
		const previouslyOpenItem = openItem;
		closeTimer = window.setTimeout(() => {
			closeTimer = null;
			const candidates = new Set([item, previouslyOpenItem, openItem]);
			candidates.forEach((candidate) => {
				if (!candidate) return;
				const stillHovered = candidate.matches(":hover");
				const stillFocused = candidate.contains(document.activeElement);
				if (!stillHovered && !stillFocused) {
					setItemOpen(candidate, false);
					if (openItem === candidate) {
						openItem = null;
						stopFeaturedRotation();
					}
				}
			});
		}, 200);
	};

	const bindMegaMenu = () => {
		document.querySelectorAll(".imek-category-item").forEach((item) => {
			const trigger = item.querySelector(".imek-category-trigger");
			const menu = item.querySelector(".imek-mega-menu");
			if (!trigger || !menu || item.dataset.megaMenuBound) {
				return;
			}

			item.dataset.megaMenuBound = "1";
			trigger.setAttribute("aria-haspopup", "true");
			trigger.setAttribute("aria-expanded", "false");
			let coarseTriggerPress = false;
			let coarseTapWasOpen = false;
			const featuredMedia = menu.querySelector("[data-imek-mega-media]");

			featuredMedia?.querySelectorAll("img").forEach((image) => {
				image.addEventListener("error", () => {
					const slide = image.closest("[data-imek-mega-slide]");
					if (!slide) return;
					slide.dataset.invalidImage = "1";
					slide.hidden = true;
					if (item.classList.contains("is-open")) {
						startFeaturedRotation(item);
					}
				});
			});

			featuredMedia?.addEventListener("mouseenter", pauseFeaturedRotation);
			featuredMedia?.addEventListener("mouseleave", () => {
				if (item.classList.contains("is-open") && !featuredMedia.contains(document.activeElement)) {
					startFeaturedRotation(item);
				}
			});
			featuredMedia?.addEventListener("focusin", pauseFeaturedRotation);
			featuredMedia?.addEventListener("focusout", (event) => {
				if (item.classList.contains("is-open") && !featuredMedia.contains(event.relatedTarget)) {
					startFeaturedRotation(item);
				}
			});

			// Hover intent prevents accidental flyouts while crossing the rail.
			// The trigger remains a normal category link.
			item.addEventListener("mouseenter", () => {
				if (isDesktop()) {
					queueOpen(item);
				}
			});

			item.addEventListener("mouseleave", () => {
				if (isDesktop()) {
					queueClose(item);
				}
			});

			// Keyboard focus opens immediately; focus may then move into the panel.
			item.addEventListener("focusin", (event) => {
				if (isDesktop()) {
					if (event.target === trigger && coarseTriggerPress) return;
					openMenu(item);
				}
			});

			item.addEventListener("focusout", (event) => {
				if (isDesktop() && !item.contains(event.relatedTarget)) {
					queueClose(item);
				}
			});

			trigger.addEventListener("keydown", (event) => {
				if (!isDesktop() || event.key !== "ArrowDown") return;
				event.preventDefault();
				openMenu(item, true);
				pauseFeaturedRotation();
			});

			trigger.addEventListener("pointerdown", (event) => {
				const coarsePointer = window.matchMedia(
					"(hover: none), (pointer: coarse)"
				).matches;
				coarseTriggerPress = isDesktop() && coarsePointer && event.button === 0;
				coarseTapWasOpen = item.classList.contains("is-open");
			});

			trigger.addEventListener("pointercancel", () => {
				coarseTriggerPress = false;
			});

			// On a coarse pointer at desktop width, the first tap previews the
			// panel and the second tap follows the category link.
			trigger.addEventListener("click", (event) => {
				const shouldPreview = coarseTriggerPress && !coarseTapWasOpen;
				coarseTriggerPress = false;
				if (shouldPreview) {
					event.preventDefault();
					openMenu(item);
				}
			});
		});
	};

	document.addEventListener("DOMContentLoaded", () => {
		placeCategoryBar();
		syncNavbarHeight();
		observeLayout();
		bindMegaMenu();
		markActiveCategory();

		// Scrolling the category rail changes the active pointer target.
		const bar = document.querySelector("[data-imek-category-bar]");
		const scrollTarget = document.querySelector(".imek-category-bar-inner") || bar;
		if (scrollTarget) {
			scrollTarget.addEventListener("scroll", () => closeMenus(), { passive: true });
		}
	});
	window.addEventListener("load", syncNavbarHeight);
	document.addEventListener("click", (event) => {
		if (!event.target.closest(".imek-category-item")) {
			closeMenus();
		}
	});
	document.addEventListener("keydown", (event) => {
		if (event.key === "Escape") {
			const focusedItem = document.activeElement?.closest(
				".imek-category-item.is-open"
			);
			if (focusedItem) {
				// Return keyboard users to the control that opened the flyout.
				// Focus first (which may re-run focusin), then close the menu so
				// focus never remains inside content that has become hidden.
				focusedItem
					.querySelector(".imek-category-trigger")
					?.focus({ preventScroll: true });
				event.preventDefault();
			}
			closeMenus();
		}
	});
	document.addEventListener("visibilitychange", () => {
		if (document.hidden) {
			pauseFeaturedRotation();
		} else if (openItem?.classList.contains("is-open")) {
			startFeaturedRotation(openItem);
		}
	});
	window.addEventListener("resize", () => {
		closeMenus();
		syncNavbarHeight();
	});
})();
